import { AbstractControl, AsyncValidatorFn, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AnyType } from '@thehubz/shared/model';
import { Observable, debounceTime, distinctUntilChanged, map, of, switchMap, take, tap } from 'rxjs';
import { removeTime } from './date';

export const emailRegexp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const nameRegexp = /[!@#$%^&*()+=\\[\]{};':"\\|,<>\\/?]/;
export const nameExcludeRegexp = /[!@#$%^*=\\[\]{};\\|<>\\?]/;

export const userApprovalPasswordValidators = [
  Validators.minLength(8),
  patternValidator(/\d/, { hasNumber: true }),
  patternValidator(/[A-Z]/, { hasCapitalCase: true }),
  patternValidator(/[a-z]/, { hasSmallCase: true }),
  patternValidator(/[!@#$§%^&*()_+-=;:|,.<>/?]/, { hasSpecialCharacters: true }),
  // should not have any other characters than the ones above
  patternValidator(/^[a-zA-Z0-9!@#$§%^&*()_+-=;:|,.<>/?]*$/, { invalidCharacters: true }),
];

export const passwordValidators = [
  Validators.required,
  Validators.minLength(8),
  patternValidator(/\d/, { hasNumber: true }),
  patternValidator(/[A-Z]/, { hasCapitalCase: true }),
  patternValidator(/[a-z]/, { hasSmallCase: true }),
  patternValidator(/[!@#$§%^&*()_+-=;:|,.<>/?]/, { hasSpecialCharacters: true }),
  // should not have any other characters than the ones above
  patternValidator(/^[a-zA-Z0-9!@#$§%^&*()_+-=;:|,.<>/?]*$/, { invalidCharacters: true }),
];

/**
 * Validator to check a valid email
 * @param control The email to check
 * @returns An object with 'invalidEmail' property sets to true
 */
export const emailValidator: ValidatorFn = (control: AbstractControl): { invalidEmail: boolean } | null => {
  if (control.value && !emailRegexp.test(control.value)) {
    return { invalidEmail: true };
  }
  return null;
};

/**
 * Validate if value exist in DB.
 */
export class ValueExistsValidator {
  static createValidator(apiCall: (value: string) => Observable<boolean>): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.valueChanges || control.pristine) {
        return of(null);
      } else {
        return control.valueChanges.pipe(
          debounceTime(400),
          distinctUntilChanged(),
          take(1),
          switchMap(() => apiCall(control.value)),
          tap(() => control.markAsTouched()),
          map((data) => (data ? { userExist: true } : null)),
        );
      }
    };
  }
}

/**
 * Validator to check a valid name
 * @param control The name to check
 * @returns An object with 'invalidName' property sets to true
 */
export const nameValidator: ValidatorFn = (control: AbstractControl): { invalidName: boolean } | null => {
  if (control.value && nameRegexp.test(control.value)) {
    return { invalidName: true };
  }
  return null;
};

/**
 * Validator to check a valid name (excluding character &()+':",/)
 * @param control The name to check
 * @returns An object with 'invalidName' property sets to true
 */
export const nameValidatorExclude: ValidatorFn = (control: AbstractControl): { invalidName: boolean } | null => {
  if (control.value && nameExcludeRegexp.test(control.value)) {
    return { invalidName: true };
  }
  return null;
};

/**
 * Validator to check project and owner
 * @param projectKey The function to get the project
 * @param ownerKey The function to get the owner
 * @returns An error if there are no project and no owner
 */
export function validateNewActionResponsible(projectKey: string, ownerKey: string) {
  return (group: UntypedFormGroup) => {
    const project = group.controls[projectKey];
    const owner = group.controls[ownerKey];

    if (!project.value && !owner.value) {
      return owner.setErrors({ requiredEmptyProject: true });
    }
  };
}

/**
 * Validator to check action and owner
 * @param actionKey The function to get the action
 * @param ownerKey The function to get the owner
 * @returns An error if there are no action and no owner
 */
export function validateNewTaskResponsible(actionKey: string, ownerKey: string) {
  return (group: UntypedFormGroup) => {
    const action = group.controls[actionKey];
    const owner = group.controls[ownerKey];

    if (!action.value && !owner.value) {
      return owner.setErrors({ requiredEmptyAction: true });
    }
  };
}

/**
 * Validator to check if the passwords match
 * @param passwordKey The password
 * @param passwordConfirmationKey The confirmation password
 * @returns An error if the password and the confirmation password are not equals
 */
export function matchingPasswords(passwordKey: string, passwordConfirmationKey: string) {
  return (group: UntypedFormGroup) => {
    const password = group.controls[passwordKey];
    const passwordConfirmation = group.controls[passwordConfirmationKey];
    if (password.value !== passwordConfirmation.value) {
      return passwordConfirmation.setErrors({ mismatchedPasswords: true });
    }
  };
}

/**
 * Validator to test a regular expression
 * @param regex The regular expression
 * @param error The error to return if the value don't match the regular expression
 * @returns null if there is no value. The error if the value don't match the regular expression
 */
export function patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
  return (control: AbstractControl): Record<string, AnyType> | null => {
    if (!control.value) {
      return null;
    }
    return regex.test(control.value) ? null : error;
  };
}

export function valueInList(list: AnyType[], error: ValidationErrors): ValidatorFn {
  return (control: AbstractControl): Record<string, AnyType> | null => {
    const val = control.value;
    return !val || list.some((x) => x.value_key === val) ? null : error;
    // return 'Do stuff with the value because `input` and `categoryList` are now in scope!'
  };
}

/**
 * Validator to check derivable
 * @param actionKey The function to get the action
 * @param ownerKey The function to get the owner
 * @returns An error if there are no action and no owner
 */
export function validateDeli(error: ValidationErrors) {
  return (group: UntypedFormGroup) => {
    const isOther = group.parent && group.parent.value && group.parent.value.channel === 'root.other.';
    return !group.value && !isOther ? error : null;
  };
}

/**
 * Add dateInvalid error
 * @param dateControl
 * @returns
 */
export function minDateValidator(dateControl: AbstractControl): ValidatorFn {
  return (): Record<string, AnyType> | null => {
    const dateValue = dateControl.value ? removeTime(dateControl.value) : undefined;
    if (!dateValue) {
      return null;
    }
    const today = removeTime(new Date());
    if (dateValue < today) {
      return { dateInvalid: true };
    }
    return null;
  };
}
